import React, { useState } from 'react';
import type { ChecklistItem, ItemResult, ItemStatus } from '../../types';
import { GaugeInput } from './GaugeInput';
import { PhotoCapture } from './PhotoCapture';
import {
  CheckCircle2,
  XCircle,
  CheckSquare,
  HelpCircle,
  MessageSquare,
  Camera,
  AlertTriangle,
  FileText,
} from 'lucide-react';

interface ChecklistCardProps {
  item: ChecklistItem;
  result?: ItemResult;
  onUpdateResult: (result: ItemResult) => void;
  disabled?: boolean;
}

export const ChecklistCard: React.FC<ChecklistCardProps> = ({
  item,
  result,
  onUpdateResult,
  disabled = false,
}) => {
  const [showNotes, setShowNotes] = useState<boolean>(Boolean(result?.notes));
  const [showPhoto, setShowPhoto] = useState<boolean>(Boolean(result?.photoUrl));

  // Current item result or fallback
  const currentStatus: ItemStatus = result?.status || 'OK';
  const isAbnormal: boolean = result?.isAbnormal || false;

  const handleStatusChange = (status: ItemStatus) => {
    if (disabled) return;
    const isNowAbnormal = status === 'NG';
    onUpdateResult({
      itemId: item.id,
      itemNo: item.itemNo,
      frequency: item.frequency,
      taskName: item.taskNameTh,
      criteria: item.criteriaTh,
      status,
      numericValue: result?.numericValue,
      measuredValue: result?.measuredValue,
      selectedOption: result?.selectedOption,
      isAbnormal: isNowAbnormal,
      notes: result?.notes,
      photoUrl: result?.photoUrl,
    });
  };

  const handleGaugeChange = (
    value: number | undefined,
    outOfTolerance: boolean,
    warningMessage?: string
  ) => {
    // When gauge is out of tolerance, automatically flag as NG!
    const autoStatus: ItemStatus = outOfTolerance ? 'NG' : 'OK';
    onUpdateResult({
      itemId: item.id,
      itemNo: item.itemNo,
      frequency: item.frequency,
      taskName: item.taskNameTh,
      criteria: item.criteriaTh,
      status: autoStatus,
      numericValue: value,
      measuredValue: value !== undefined ? `${value} ${item.unit || 'MPa'}` : undefined,
      selectedOption: result?.selectedOption,
      isAbnormal: outOfTolerance,
      notes: result?.notes,
      photoUrl: result?.photoUrl,
    });
  };

  const handleOptionSelect = (option: string) => {
    if (disabled) return;
    // Common abnormal options: "ชำรุด", "รั่วซึม", "อุดตัน", "แห้งผิดปกติ"
    const abnormalOptions = ['ชำรุด', 'รั่วซึม', 'อุดตัน', 'แห้งผิดปกติ'];
    const isOptionAbnormal = abnormalOptions.includes(option);
    const autoStatus: ItemStatus = isOptionAbnormal ? 'NG' : 'OK';

    onUpdateResult({
      itemId: item.id,
      itemNo: item.itemNo,
      frequency: item.frequency,
      taskName: item.taskNameTh,
      criteria: item.criteriaTh,
      status: autoStatus,
      numericValue: result?.numericValue,
      measuredValue: option,
      selectedOption: option,
      isAbnormal: isOptionAbnormal,
      notes: result?.notes,
      photoUrl: result?.photoUrl,
    });
  };

  const handleDoneToggle = () => {
    if (disabled) return;
    const isCurrentlyDone = currentStatus === 'DONE';
    const nextStatus: ItemStatus = isCurrentlyDone ? 'NG' : 'DONE';
    onUpdateResult({
      itemId: item.id,
      itemNo: item.itemNo,
      frequency: item.frequency,
      taskName: item.taskNameTh,
      criteria: item.criteriaTh,
      status: nextStatus,
      numericValue: result?.numericValue,
      measuredValue: nextStatus === 'DONE' ? 'ทำแล้ว (Done)' : 'ยังไม่ทำ',
      selectedOption: nextStatus === 'DONE' ? 'ทำแล้ว' : undefined,
      isAbnormal: nextStatus === 'NG',
      notes: result?.notes,
      photoUrl: result?.photoUrl,
    });
  };

  const handleNotesChange = (notes: string) => {
    onUpdateResult({
      itemId: item.id,
      itemNo: item.itemNo,
      frequency: item.frequency,
      taskName: item.taskNameTh,
      criteria: item.criteriaTh,
      status: currentStatus,
      numericValue: result?.numericValue,
      measuredValue: result?.measuredValue,
      selectedOption: result?.selectedOption,
      isAbnormal,
      notes,
      photoUrl: result?.photoUrl,
    });
  };

  const handlePhotoChange = (photoUrl?: string) => {
    onUpdateResult({
      itemId: item.id,
      itemNo: item.itemNo,
      frequency: item.frequency,
      taskName: item.taskNameTh,
      criteria: item.criteriaTh,
      status: currentStatus,
      numericValue: result?.numericValue,
      measuredValue: result?.measuredValue,
      selectedOption: result?.selectedOption,
      isAbnormal,
      notes: result?.notes,
      photoUrl,
    });
  };

  // Card Border Styling based on status
  const cardBorderClass = isAbnormal || currentStatus === 'NG'
    ? 'border-2 border-rose-500 bg-rose-50/40 shadow-sm'
    : currentStatus === 'DONE' || currentStatus === 'OK'
    ? 'border border-slate-200 hover:border-slate-300 bg-white shadow-xs'
    : 'border border-slate-200 bg-white shadow-xs';

  return (
    <div className={`rounded-xl p-4 sm:p-5 transition-all space-y-4 ${cardBorderClass}`}>
      {/* Header: Item Number & Task Name */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-blue-700 font-mono font-black text-sm border border-blue-200 shrink-0">
            {item.itemNo}
          </span>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
              {item.taskNameTh}
            </h3>
            {item.taskNameEn && (
              <p className="text-xs text-slate-500 mt-0.5 font-normal">
                {item.taskNameEn}
              </p>
            )}
          </div>
        </div>

        {item.hoursThreshold && (
          <span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-800 border border-amber-200 font-mono text-xs font-bold shrink-0">
            {item.hoursThreshold.toLocaleString()} ชม.
          </span>
        )}
      </div>

      {/* Criteria & Checking Method Box */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
        <div>
          <span className="text-slate-500 font-semibold block mb-0.5">
            เกณฑ์มาตรฐาน (Criteria):
          </span>
          <span className="text-slate-800 font-medium leading-relaxed">
            {item.criteriaTh}
          </span>
        </div>
        <div>
          <span className="text-slate-500 font-semibold block mb-0.5">
            วิธีตรวจเช็ก (Method):
          </span>
          <span className="text-blue-700 font-semibold">
            {item.methodTh}
          </span>
        </div>
      </div>

      {/* Dynamic Input Type Rendering */}
      <div className="pt-1">
        {/* 1. Numeric Gauge Input */}
        {item.inputType === 'numeric_gauge' && (
          <GaugeInput
            itemId={item.id}
            itemNo={item.itemNo}
            unit={item.unit || 'MPa'}
            value={result?.numericValue}
            minValue={item.minValue}
            maxValue={item.maxValue}
            criticalLimit={item.criticalLimit}
            disabled={disabled}
            onChange={handleGaugeChange}
          />
        )}

        {/* 2. Select Option Multi-Choice Pills */}
        {item.inputType === 'select_option' && item.options && (
          <div className="space-y-1.5">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block">
              เลือกผลการตรวจสอบ (Select Result)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {item.options.map((opt) => {
                const isSelected = result?.selectedOption === opt;
                const isNegative = ['ชำรุด', 'รั่วซึม', 'อุดตัน', 'แห้งผิดปกติ'].includes(opt);

                let btnStyle = 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 shadow-2xs';
                if (isSelected) {
                  btnStyle = isNegative
                    ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                    : 'bg-blue-600 text-white border-blue-600 shadow-sm';
                }

                return (
                  <button
                    key={opt}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleOptionSelect(opt)}
                    className={`px-4 py-3 rounded-lg font-bold text-sm border transition-all min-h-[48px] select-none active:scale-95 flex items-center justify-center gap-2 ${btnStyle}`}
                  >
                    <span>{opt}</span>
                    {isSelected && (
                      <CheckCircle2 className="w-4 h-4 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 3. Action Checkbox (Done Action) */}
        {item.inputType === 'done_checkbox' && (
          <div>
            <button
              type="button"
              disabled={disabled}
              onClick={handleDoneToggle}
              className={`w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl font-bold text-base border transition-all min-h-[52px] select-none active:scale-[0.99] ${
                currentStatus === 'DONE'
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                  : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400 hover:bg-slate-50 shadow-xs'
              }`}
            >
              <CheckSquare
                className={`w-6 h-6 ${
                  currentStatus === 'DONE' ? 'text-white' : 'text-slate-400'
                }`}
              />
              <span>
                {currentStatus === 'DONE'
                  ? 'ดำเนินการเสร็จสิ้นแล้ว (Done)'
                  : 'กดเพื่อยืนยันการทำรายการ (Mark as Done)'}
              </span>
            </button>
          </div>
        )}

        {/* 4. Text Input */}
        {item.inputType === 'text' && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-600 block mb-1">
              บันทึกผลการตรวจสอบ
            </label>
            <input
              type="text"
              disabled={disabled}
              value={result?.measuredValue || ''}
              onChange={(e) =>
                onUpdateResult({
                  itemId: item.id,
                  itemNo: item.itemNo,
                  frequency: item.frequency,
                  taskName: item.taskNameTh,
                  criteria: item.criteriaTh,
                  status: currentStatus,
                  measuredValue: e.target.value,
                  isAbnormal,
                  notes: result?.notes,
                  photoUrl: result?.photoUrl,
                })
              }
              placeholder="กรอกผลการตรวจสอบ..."
              className="w-full bg-white border border-slate-300 rounded-lg px-3.5 py-3 text-sm text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none min-h-[48px] shadow-xs"
            />
          </div>
        )}
      </div>

      {/* Segmented Quick Status Selector Bar (OK, NG, Done, N/A) */}
      <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0">
          {/* OK Toggle */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleStatusChange('OK')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg font-bold text-xs min-h-[44px] transition-all select-none active:scale-95 ${
              currentStatus === 'OK' && !isAbnormal
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            OK
          </button>

          {/* NG Toggle */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleStatusChange('NG')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg font-bold text-xs min-h-[44px] transition-all select-none active:scale-95 ${
              currentStatus === 'NG' || isAbnormal
                ? 'bg-rose-600 text-white shadow-sm animate-pulse'
                : 'text-slate-600 hover:text-rose-600 hover:bg-white'
            }`}
          >
            <XCircle className="w-4 h-4" />
            NG (Defect)
          </button>

          {/* Done Toggle */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleStatusChange('DONE')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg font-bold text-xs min-h-[44px] transition-all select-none active:scale-95 ${
              currentStatus === 'DONE'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-blue-600 hover:bg-white'
            }`}
          >
            <CheckSquare className="w-4 h-4" />
            Done
          </button>

          {/* N/A Toggle */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => handleStatusChange('NA')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg font-bold text-xs min-h-[44px] transition-all select-none active:scale-95 ${
              currentStatus === 'NA'
                ? 'bg-slate-700 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900 hover:bg-white'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            N/A
          </button>
        </div>

        {/* Action Toggles for Notes and Camera Evidence */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowNotes(!showNotes)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border min-h-[44px] transition-colors shadow-2xs ${
              result?.notes
                ? 'bg-blue-50 text-blue-700 border-blue-300'
                : 'bg-white text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            <span>หมายเหตุ</span>
            {result?.notes && <span className="w-2 h-2 rounded-full bg-blue-600" />}
          </button>

          <button
            type="button"
            onClick={() => setShowPhoto(!showPhoto)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border min-h-[44px] transition-colors shadow-2xs ${
              result?.photoUrl
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                : currentStatus === 'NG'
                ? 'bg-amber-50 text-amber-800 border-amber-300 animate-pulse'
                : 'bg-white text-slate-600 border-slate-200 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Camera className="w-4 h-4" />
            <span>รูปภาพ</span>
            {result?.photoUrl && <span className="w-2 h-2 rounded-full bg-emerald-600" />}
          </button>
        </div>
      </div>

      {/* Expandable Notes Input Field */}
      {showNotes && (
        <div className="pt-2 animate-in fade-in duration-150">
          <label className="text-xs font-bold text-slate-700 block mb-1">
            หมายเหตุหรืออาการผิดปกติเพิ่มเติม:
          </label>
          <textarea
            rows={2}
            disabled={disabled}
            value={result?.notes || ''}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="ระบุรายละเอียด เช่น ระดับน้ำมันหยด หรือเสียงผิดปกติ..."
            className="w-full bg-white border border-slate-300 rounded-lg p-3 text-xs text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none shadow-xs placeholder-slate-400"
          />
        </div>
      )}

      {/* Expandable Photo Capture Component */}
      {showPhoto && (
        <div className="pt-2 animate-in fade-in duration-150">
          <PhotoCapture
            itemId={item.id}
            photoUrl={result?.photoUrl}
            onChange={handlePhotoChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};
